import { useEffect, useState } from 'react';
import { getEntireData, getQuestionsWithoutAdditional } from './thunks';
import { useDispatch, useSelector } from 'react-redux';
import { updateCurrentService, setQuizStarted, setConvrTestVariations } from './reducers/quiz/quiz.actions';
import { togglePreloader, setInitialPageIndex, setQuizWasOpened } from './reducers/main/main.actions';
import { addCustomContent, addTestVariations, defineClientData, replaceHeaderPhone, updateCalculator, updateCustomLeadParams } from './reducers/client/client.actions';
import store from './store';
import { EVENT_OPEN_QUIZ, EVENT_DEFINE_SERVICE, DEMO_CLIENT_ID, EVENT_START_QUIZ, EVENT_LOAD_QUIZ, EVENT_CUSTOM_CONTENT_ADD } from './constants';
import { emitEvent, quizEventEmitter } from './event-emitter';
import { Service } from './models/service.model';
import { RootState } from './store';
import { Sections } from './models/calculator-content.model';
import services from './utils/services';
import { applyTest } from './utils/abTester';
import { ABTest } from './models/ab-test.model';
import { set as setState } from './state-switcher';
import { StateType } from './reducers/history/enums/state-type.enum';
import { YaMetrikaCounter } from './ya-metrika';
import { mockTestData } from "./mock-test-data";
import { ConvrTestVariation } from './interfaces/convr-test-variation.interface';
import { apiService } from './services/api.service';
import useResizeObserver from '@react-hook/resize-observer'
import { useCallback, useLayoutEffect } from "react";
import { captureException } from '@sentry/react';

export function useSize(recalcDep: any = true) {
	const [size, setSize] = useState<DOMRect>();
	const [target, setTarget] = useState<HTMLDivElement | null>(null);

	const getNode = useCallback((node: HTMLDivElement) => {
		setTarget(node);
	}, []);

	useLayoutEffect(() => {
		if (target && recalcDep) setSize(target.getBoundingClientRect())
	}, [target, recalcDep]);

	useResizeObserver(target, entry => {
		const rect = target?.getBoundingClientRect();
		if (target && rect) { setSize({ ...rect, top: rect.top, width: entry.target.clientWidth, height: entry.target.clientHeight }); }
	});

	return { size, getNode };
};

export function usePluginTests() {
	const variations = useSelector((state: RootState) => state.client?.testVariations);
	const [state, setState] = useState(variations);

	useEffect(() => {
		setState(variations);
	}, [variations]);

	return state;
}

export function useMockTests() {
	const dispatch = useDispatch();
	const [variations, setVariations] = useState<ConvrTestVariation[]>([]);

	useEffect(() => {
		dispatch(setConvrTestVariations(mockTestData));
		setVariations(mockTestData);
	}, [dispatch]);

	return variations;
}

export function useEntireData() {
	const { clientId } = store.getState().client || {};

	useEffect(() => {
		if (!clientId) return;
		store.dispatch(getEntireData(clientId));
	}, [clientId]);
}

export function usePostMessageParams() {
	const currentService = useSelector((state: RootState) => state.quiz.currentService);

	useEffect(() => {
		window.removeEventListener('message', handlePostMessage);
		window.addEventListener('message', handlePostMessage);
		return () => window.removeEventListener('message', handlePostMessage);

		function handlePostMessage({ data }: any) {
			if (!data) return;

			try {
				var message = JSON.parse(data);
			} catch (e) { return }

			if (message.access != 'dentolo') return;

			const { type, payload } = message;

			switch (type) {
				case 'INIT': {
					const service = payload.serviceList.length > 1 ? 'selection' : payload.serviceList[0];

					store.dispatch(updateCurrentService(service));
					store.dispatch(defineClientData(payload));


					if (payload.initialPageIndex != undefined) {
						setState({ type: payload.initialPageIndex });
						store.dispatch(setInitialPageIndex(payload.initialPageIndex));
					}

					if (service != 'selection') emitEvent(EVENT_DEFINE_SERVICE, { service });

					return;
				}

				case 'OPEN': {
					const { started } = store.getState().quiz || {};
					if (started) return;

					if (payload) {
						emitEvent(EVENT_OPEN_QUIZ, { trigger: String(payload.trigger) });
						store.dispatch(setInitialPageIndex(payload.initialPageIndex));
						store.dispatch(updateCustomLeadParams({ openTrigger: String(payload.trigger) }))
						store.dispatch(setQuizWasOpened(true));

						if (payload.initialPageIndex > 0) {
							if (payload.initialPageIndex == 99) {
								setState({ type: StateType.SIGN_UP });
								return;
							}

							if (currentService == 'selection') setState({ type: StateType.SELECTION });
							else {
								setState({ type: StateType.QUESTIONS });
								emitEvent(EVENT_START_QUIZ);
								if (!store.getState().quiz.started) store.dispatch(setQuizStarted(true));
							}
						} else setState({ type: payload.initialPageIndex });
					}
					return;
				}

				case 'REPLACE_PHONE': {
					if (payload?.phone) store.dispatch(replaceHeaderPhone(payload.phone));
					return;
				}

				case 'CUSTOM_LEAD_PARAMS': {
					if (payload) store.dispatch(updateCustomLeadParams(payload.customLeadParams));
					return;
				}

				case 'ENABLE_WEBVISOR': {
					const counter = new YaMetrikaCounter();
					counter.init(message.payload.counter, message.payload.domain);
					return;
				}

				case 'TEST_VARIATIONS': {
					if (payload) store.dispatch(addTestVariations(payload.testVariations))
					return;
				}

				case 'CUSTOM_CONTENT': {
					store.dispatch(addCustomContent(payload?.customContent || {}));
					quizEventEmitter.emit(EVENT_CUSTOM_CONTENT_ADD);
					return;
				}

				case 'SENTRY_ERROR': {
					if (payload) {
						console.error(payload);
						captureException(new Error(payload.error));
					}
					return;
				}

				case "CONVR_CONFIRM": {
					const { lead } = store.getState().client || {};
					if (lead?._id) apiService.updateLead(lead?._id as string, { konvr_confirm: true });
					break;
				}

				default: return;
			}
		}
	}, [currentService]);
}

export const useCalculator = <T>(service: Service): T => {
	const { clientId, calculator } = useSelector((state: RootState) => state.client) || {};
	const dispatch = useDispatch();

	useEffect(() => {
		const fetchData = async () => {
			const data = await apiService.getClientData(clientId as string, service);
			const calc = data[service].calculator;
			dispatch(updateCalculator({ [service]: calc }));
		};
		fetchData();
	}, [clientId, service, dispatch]);

	return calculator ? (calculator[service] || {} as T) : ({} as T);
};

// export const useAbTest = () => {
// 	const { clientId } = useSelector((state:RootState) => state.client) || {};
// 	const dispatch = useDispatch();

// 	useEffect(() => {
// 		if (clientId) getTests(clientId).then((tests:ABTest[]) => {
// 			if (tests) tests.forEach(test => applyTest(test, dispatch));
// 		});
// 	}, [clientId, dispatch]);
// }

// function getTests(clientId:string):Promise<ABTest[]> {
// 	return http.get(`ab-tests/?clientId=${clientId}&common=1`, addHeaders()).then(({data}) => {
// 		if (data?.length > 0) return data;
// 		return [];
// 	}).catch(() => Promise.reject());
// }

export const useDevelopment = async (service: Service | 'selection' = 'selection') => {
	const dispatch = useDispatch();

	useEffect(() => {
		if (process.env.NODE_ENV == 'production') return;
		const serviceList = service == 'selection' ? Object.keys(services).filter(s => s != 'selection') : [service];
		dispatch(updateCurrentService(service));
		dispatch(defineClientData({ clientId: DEMO_CLIENT_ID, serviceList }));

	}, [dispatch, service]);
}

export const useCalculatorContent = (service: Service): Sections[] => {
	const { calculator } = useSelector((state: RootState) => state.client) || {};
	const [content, setContent]: [Sections[], Function] = useState([]);
	const dispatch = useDispatch();

	const getCalcContent = useCallback(async () => {
		return apiService.getDefaultCalculatorContent(service);
	}, [service]);

	useEffect(() => {
		dispatch(togglePreloader(true, 'transparent'));
		getCalcContent().then(defaultCalcContent => {
			let content = defaultCalcContent;
			if (calculator?.content) content = calculator.content;
			setContent(content);
		}).finally(() => dispatch(togglePreloader(false)));

	}, [dispatch, getCalcContent]);

	return content;
}

export const useWindowSize = () => {
	const [windowSize, setWindowSize] = useState<{ width: number, height: number }>({
		width: window.innerWidth,
		height: window.innerHeight,
	});

	useEffect(() => {
		function handleResize() {
			setWindowSize({
				width: window.innerWidth,
				height: window.innerHeight,
			});
		}

		window.addEventListener("resize", handleResize);

		handleResize();

		return () => window.removeEventListener("resize", handleResize);
	}, []);

	return windowSize;
}